<template>
    <div class="custom-input-wrapper" :class="[classContainerAdd, { isNotrequired: !required }]">
        <!-- Label -->
        <BaseLabel
            v-if="label && !floatingLabel"
            :customLabelClass="customClassLabel"
            :customSizeLabel="customSizeLabel"
            :label="label"
            :for-id="inputId"
            :required="required"
        >
            <template #icon v-if="$slots['label-icon']">
                <slot name="label-icon" />
            </template>
        </BaseLabel>

        <!-- Tooltip -->
        <InfoMessageBox v-if="tooltipMsg" class="ppv2-mt-2" :message="tooltipMsg" color="info" />

        <!-- Description -->
        <p v-if="description" class="ppv2-text-xs ppv2-text-gray-500">
            {{ description }}
        </p>

        <!-- Input Container -->
        <div
            class="input-container ppv2-relative"
            :class="[classInputAdd, state, { 'has-floating-label': floatingLabel && label }]"
        >
            <!-- Floating Label -->
            <label
                v-if="floatingLabel && label"
                class="floating-label ppv2-text-opacity-70 hover:ppv2-text-opacity-100 poppinsMedium"
                :style="[
                    { fontSize: normalizeFontSize(customSizeLabel || 10, true) },
                    floatingLabelBg ? { backgroundColor: floatingLabelBg } : {},
                ]"
            >
                {{ label }}<span class="text-purple" v-if="required">*</span>
            </label>
            <!-- Left Icon -->
            <div v-if="leftIcon || $slots.leftIcon" class="ppv2-flex ppv2-items-center ppv2-mr-2">
                <slot name="leftIcon">
                    <component v-if="leftIcon" :is="leftIcon" class="ppv2-w-4 ppv2-h-4 ppv2-text-gray-700" />
                </slot>
            </div>

            <!-- Input Field -->
            <input
                :id="inputId"
                :name="inputName"
                :type="actualInputType"
                :value="computedValue"
                :placeholder="placeholder"
                :disabled="disabled"
                :readonly="readonly"
                :autocomplete="computedAutocomplete"
                :data-form-type="dataFormType"
                :step="type === 'number' ? step : undefined"
                v-bind="$attrs"
                class="custom-input ppv2-text-black"
                :style="[{ fontSize: normalizeFontSize(customSizeLabel, true) }]"
                @input="onInput"
                @blur="$emit('blur', $event)"
                @focus="$emit('focus', $event)"
                @keyup.enter="$emit('enter', $event)"
                @change="$emit('change', $event)"
            />

            <!-- Right Icon or Password Toggle -->
            <div
                v-if="rightIcon || suffix || $slots.rightIcon || type === 'password'"
                class="ppv2-flex ppv2-items-center ppv2-justify-space-between ppv2-ml-2"
            >
                <slot name="rightIcon">
                    <span
                        v-if="suffix"
                        class="icon rightIcon"
                        :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                        >{{ suffix }}</span
                    >
                    <component
                        v-else-if="rightIcon"
                        :is="rightIcon"
                        class="icon rightIcon"
                        :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    />
                    <!-- ✅ Valid Icon -->
                    <!--span
                        v-if="state === 'valid'"
                        class="icon valid"
                        :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                        >✔</span
                    -->

                    <!-- ⚠️ Error Icon -->
                    <span
                        v-if="state === 'error'"
                        class="icon error"
                        :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                        >!</span
                    >
                    <button
                        v-else-if="type === 'password'"
                        type="button"
                        class="ppv2-flex ppv2-items-center ppv2-justify-center hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded-md ppv2-transition-colors"
                        @click.stop="togglePassword"
                    >
                        <Eye v-if="!showPassword" :size="normalizeSize(18)" class="ppv2-text-[#94a3b8]" />
                        <EyeOff v-else :size="normalizeSize(18)" class="ppv2-text-[#94a3b8]" />
                    </button>
                </slot>
            </div>
            <div v-else>
                <!-- ✅ Valid Icon -->
                <!--span v-if="state === 'valid'" class="icon valid" :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    >✔</span
                -->

                <!-- ⚠️ Error Icon -->
                <span
                    v-if="state === 'error'"
                    class="icon error"
                    :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }"
                    >!</span
                >
            </div>
            <span v-if="iconIA" class="icon ia" :style="{ fontSize: normalizeFontSize(fontSizeIconRight, true) }">
                ><img src="/media/bo/icons/ai/ai.svg" v-if="!isDark" /><img src="/media/svg/aiDark.svg" v-if="isDark"
            /></span>
        </div>

        <!-- Error Message -->
        <p v-if="error || errorText || state === 'error'" class="error-text">
            {{ error }}
        </p>

        <!-- Help Text -->
        <p
            v-if="helpText"
            class="ppv2-text-xs ppv2-text-black ppv2-text-opacity-60 ppv2-p-2 ppv2-bg-black ppv2-bg-opacity-5 ppv2-rounded-[10px]"
        >
            {{ helpText }}
        </p>
    </div>
</template>

<script>
import BaseLabel from './BaseLabel.vue'
import InfoMessageBox from './InfoMessageBox.vue'
import normalizeSize from '../../utils/normalizeSize'
import { Eye, EyeOff } from 'lucide-vue'
import normalizeFontSize from '../../utils/normalizeFontSize'

export default {
    name: 'BaseInput',
    components: {
        BaseLabel,
        InfoMessageBox,
        Eye,
        EyeOff,
    },
    props: {
        value: [String, Number],
        label: String,
        description: String,
        placeholder: String,
        type: {
            type: String,
            default: 'text',
        },
        errorText: String,
        state: {
            type: String,
            default: 'default', // 'default', 'disabled', 'active', 'valid', 'error'
        },
        required: {
            type: Boolean,
            default: false,
        },
        disabled: {
            type: Boolean,
            default: false,
        },

        classInputAdd: {
            type: String,
            default: '',
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        tooltipMsg: {
            type: String,
            default: '',
        },
        error: String,
        helpText: String,
        leftIcon: String,
        rightIcon: String,
        suffix: String,
        size: {
            type: String,
            default: 'sm',
            validator: (value) => ['sm', 'md', 'lg'].includes(value),
        },

        customClassLabel: {
            type: String,
            default: '',
        },
        customSizeLabel: {
            type: Number,
            default: 15,
        },
        classContainerAdd: {
            type: String,
            default: '',
        },
        showIcon: Boolean,
        iconIA: Boolean,
        autocomplete: {
            type: String,
            default: 'off',
        },
        customClassSize: {
            type: [String, Array, Object],
            default: '',
        },
        step: {
            type: [String, Number],
            default: 'any',
        },
        fontSizeIconRight: {
            type: Number,
            default: 15,
        },
        floatingLabel: {
            type: Boolean,
            default: false,
        },
        floatingLabelBg: {
            type: String,
            default: '',
        },
    },
    data() {
        return {
            internalInputId: `input-${Math.random().toString(36).substr(2, 9)}`,
            showPassword: false,
        }
    },
    computed: {
        isDark() {
            return this.theme.mode === 'dark'
        },
        inputId() {
            return this.internalInputId
        },
        inputName() {
            return `input-${this.internalInputId}`
        },
        computedValue() {
            return this.value != null ? String(this.value) : ''
        },
        actualInputType() {
            if (this.type === 'password') {
                return this.showPassword ? 'text' : 'password'
            }
            return this.type
        },
        computedAutocomplete() {
            if (this.autocomplete === 'off') {
                return 'new-password'
            }
            return this.autocomplete
        },
        dataFormType() {
            if (this.autocomplete === 'off') {
                return 'other'
            }
            return null
        },
        inputClasses() {
            const baseClasses = [
                'ppv2-h-[49px]',
                'ppv2-block',
                'ppv2-w-full',
                'ppv2-border',
                'ppv2-border-gray-300',
                'ppv2-rounded-lg',
                'ppv2-placeholder-gray-400',
                'ppv2-focus:outline-none',
                'ppv2-focus:ring-2',
                'ppv2-focus:ring-blue-500',
                'ppv2-focus:border-blue-500',
                'ppv2-transition-colors',
                'border',
            ]

            if (this.leftIcon || this.$slots.leftIcon) {
                baseClasses.push('ppv2-pl-10')
            } else {
                baseClasses.push('ppv2-pl-3')
            }

            if (this.rightIcon || this.suffix || this.$slots.rightIcon || this.type === 'password') {
                baseClasses.push('ppv2-pr-10')
            } else {
                baseClasses.push('ppv2-pr-3')
            }

            switch (this.size) {
                case 'sm':
                    baseClasses.push('ppv2-py-1', 'ppv2-text-sm')
                    break
                case 'lg':
                    baseClasses.push('ppv2-py-3', 'ppv2-text-lg')
                    break
                default:
                    baseClasses.push('ppv2-py-2')
            }

            if (this.error) {
                baseClasses.push('ppv2-border-red-300', 'ppv2-focus:ring-red-500', 'ppv2-focus:border-red-500')
            }

            if (this.disabled) {
                baseClasses.push('ppv2-bg-gray-50', 'ppv2-text-gray-500', 'ppv2-cursor-not-allowed')
            } else {
                baseClasses.push('ppv2-bg-white')
            }
            if (this.customClassSize && this.customClassSize.toString().match(/rounded/)) {
                const index = baseClasses.indexOf('ppv2-rounded-lg')
                if (index !== -1) baseClasses.splice(index, 1)
            }
            return baseClasses.join(' ')
        },
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        onInput(event) {
            let value = event.target.value
            if (this.type === 'number') {
                value = value !== '' ? Number(value) : null
            }
            this.$emit('input', value)
        },
        togglePassword() {
            this.showPassword = !this.showPassword
        },
    },
}
</script>

<style scoped>
.custom-input-wrapper {
    display: flex;
    flex-direction: column;
    gap: 8px;
    width: 100%;
    margin-bottom: 20px;
    opacity: 0.7;
    transition: opacity 0.2s ease;
}
.custom-input-wrapper:hover,
.custom-input-wrapper:focus-within {
    opacity: 1;
}
.input-container {
    display: flex;
    align-items: center;
    border-radius: 12px;
    border: 1px solid #9c9af880;
    padding: 8.5px 12px;
    transition: all 0.2s ease;
}

.input-container.has-floating-label {
    margin-top: 8px;
}

.floating-label {
    position: absolute;
    top: 0;
    transform: translateY(-65%);
    left: 12px;
    padding: 0 4px;
    z-index: 10;
    pointer-events: none;
    line-height: 1;
    opacity: 1 !important;
    white-space: nowrap;
    background-color: #fff;
    font-size: inherit;
}

.dark .floating-label {
    background-color: #060b28; /* Solid background for dark mode to cut the border */
}

.input-container input.custom-input {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
}

.input-container.disabled {
    opacity: 0.4;
    cursor: not-allowed;
}

.input-container.error {
    border-color: #ed4b9e !important;
    background-color: initial !important;
}

.input-container.active {
    border-color: #9c9af8b2;
}

/*.input-container.valid {
    border-color: #31d0aa;
}*/

.input-container.error {
    border-color: #ed4b9e !important;
    background-color: initial !important;
}

.icon {
    margin-left: 8px;
    background-color: transparent;
}
.ppv2-h-\[75px\] input {
    height: 75px;
}
/*.icon.valid {
    color: #31d0aa;
}*/
.icon.rightIcon {
    color: #94a3b8;
}

.icon.error {
    display: inline-block;
    color: #ed4b9e;
    background-color: initial !important;
    border: 1px solid;
    border-radius: 50%;
    width: 17px;
    height: 17px;
    text-align: center;
    line-height: 17px;
    font-size: 15px;
}

.icon.ia {
    color: #7c00f080;
}
.dark .icon.ia {
    color: #9c9af880;
}

.error-text {
    color: #ed4b9e;
    font-size: clamp(10px, 0.625vw, 22px);
}
</style>
